import { ChangeEvent } from 'react';
import { useRouter } from 'next/router';

import {
  cleanupDoubleSpace,
  validateAndCleanInputText,
  validate,
} from '@keymono/utilities';
import { TLocale } from '@keymono/i18n';

import { IInputElement } from '../../types';

import { TextInput } from './TextInput';

interface IInputTextItemProps extends IInputElement {
  // Add more `InputTextElement` props here
}

/**
 * -----------------------------------------------------------------------------
 * This is used to create the underlying debounced `TextInput` that...
 * - Captures user input, validates it based on provided constraints config.
 * - Updates the parent form the debounce delay (default 1s) with errors data
 *   and updated input.
 */
export function InputTextElement({
  autoFocus,
  className,
  classNameIconEnd,
  classNameInput,
  classNameInputContainer,
  classNameLabel,
  constraints = {},
  editable,
  error,
  hasClearButton,
  helpText,
  iconEnd,
  iconStart,
  isLabelEnabled,
  label,
  labelPostFix,
  name,
  onChangeCallback,
  onError,
  onBlurCallback,
  onFocus,
  placeholder,
  showIsRequired,
  type,
  value = '',
  valuePrefix,
}: IInputTextItemProps) {
  const locale = useRouter().locale as TLocale;

  const { minLength, maxLength = 40, regExp, required, format } = constraints;

  const handleValidateOnChange = (newValue: string) => {
    const updatedText = validateAndCleanInputText({
      text: newValue,
      format,
      regExp,
      constraintType: constraints.type,
    });

    onChangeCallback({
      name,
      value: updatedText,
    });

    return updatedText;
  };

  const handleInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    handleValidateOnChange(text);
  };

  const handleInputFocus = () => {
    onError({ name, error: undefined });
    onFocus?.();
  };

  const handleInputBlur = (event: ChangeEvent<HTMLInputElement>) => {
    let updatedText = event.target.value;

    if (updatedText) {
      updatedText = cleanupDoubleSpace(updatedText);
    }

    const inputError = validate({ target: event.target, constraints, locale });

    onError({ name, error: inputError });
    onBlurCallback?.({ name, value: updatedText });
  };

  const handleClearInput = () => {
    handleValidateOnChange('');
  };

  return (
    <TextInput
      autoFocus={autoFocus}
      className={className}
      classNameIconEnd={classNameIconEnd}
      classNameInput={classNameInput}
      classNameInputContainer={classNameInputContainer}
      classNameLabel={classNameLabel}
      editable={editable}
      error={error}
      handleClearInput={handleClearInput}
      hasClearButton={hasClearButton}
      helpText={helpText}
      iconEnd={iconEnd}
      iconStart={iconStart}
      isLabelEnabled={isLabelEnabled}
      label={label}
      labelPostFix={labelPostFix}
      locale={locale}
      maxLength={maxLength}
      minLength={minLength}
      name={name}
      onBlur={handleInputBlur}
      onChange={handleInputChanged}
      onFocus={handleInputFocus}
      placeholder={placeholder}
      required={required}
      showIsRequired={showIsRequired}
      type={type}
      value={value || ''}
      valuePrefix={valuePrefix}
    />
  );
}
